import React, { useState, useEffect, useCallback } from 'react';
import type {
  VenueTable, VenueTableRequest, Section, QrCode // Types
} from '../api/api';
import {
  getTablesBySection, createTable, updateTable, deleteTable,
  assignQrCodeToTable, removeQrCodeFromTable, getUnassignedQrCodes
} from '../api/api';
import Modal from './Modal';
import Button from '../ui/Button'; // Import custom Button

interface TableListProps {
  selectedSection: Section | null;
  tables: VenueTable[];
  setTables: React.Dispatch<React.SetStateAction<VenueTable[]>>;
  isLoadingInitial: boolean; // isLoading prop from parent for initial table load
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const TableListComponent: React.FC<TableListProps> = ({
  selectedSection,
  tables,
  setTables,
  isLoadingInitial,
  setError,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTableData, setCurrentTableData] = useState<Partial<VenueTableRequest & { id?: string }>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<VenueTable | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [selectedTableForQr, setSelectedTableForQr] = useState<VenueTable | null>(null);
  const [unassignedQrs, setUnassignedQrs] = useState<QrCode[]>([]);
  const [isLoadingQrs, setIsLoadingQrs] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false); // For table CUD
  const [isUpdatingQr, setIsUpdatingQr] = useState(false); // For QR operations


  const refreshTables = useCallback(async () => {
    if (!selectedSection) return;
    try {
      const updatedTables = await getTablesBySection(selectedSection.id as string);
      setTables(Array.isArray(updatedTables) ? updatedTables : []);
      setError(null);
    } catch (err) {
      console.error('Failed to refresh tables:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh tables list.';
      setError(errorMessage);
    }
  }, [selectedSection, setTables, setError]);

  // Table CRUD
  const openAddModal = () => {
    if (!selectedSection) return;
    setCurrentTableData({ name: '', sectionId: selectedSection.id as string });
    setIsEditing(false);
    setIsModalOpen(true);
    clearError();
  };

  const openEditModal = (table: VenueTable) => {
    setCurrentTableData({ id: table.id, name: table.name, sectionId: table.sectionId as string });
    setIsEditing(true);
    setIsModalOpen(true);
    clearError();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentTableData({});
  };

  const handleTableInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentTableData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitTable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSection) {
      setError("No section selected.");
      return;
    }
    if (!currentTableData.name?.trim()) {
      setError("Table name cannot be empty.");
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const payload: VenueTableRequest = {
      name: currentTableData.name,
      sectionId: currentTableData.sectionId || (selectedSection.id as string)
    };

    try {
      if (isEditing && currentTableData.id) {
        await updateTable(currentTableData.id as string, payload);
      } else {
        await createTable(payload);
      }
      await refreshTables();
      closeModal();
    } catch (err) {
      console.error('Failed to save table:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to save table.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteConfirm = (table: VenueTable) => {
    setTableToDelete(table);
    setShowDeleteConfirm(true);
    clearError();
  };

  const closeDeleteConfirm = () => {
    setTableToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleDeleteTable = async () => {
    if (!tableToDelete || !selectedSection) return;
    setIsSubmitting(true); // Use isSubmitting for delete as well or a specific isDeletingTable
    setError(null);
    try {
      await deleteTable(tableToDelete.id as string);
      await refreshTables();
      closeDeleteConfirm();
    } catch (err) {
      console.error('Failed to delete table:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete table.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // QR Code Management
  const openQrModal = (table: VenueTable) => {
    setSelectedTableForQr(table);
    setIsQrModalOpen(true);
    clearError();
  };

  const closeQrModal = () => {
    setIsQrModalOpen(false);
    setSelectedTableForQr(null);
    setUnassignedQrs([]);
  };

  useEffect(() => {
    if (isQrModalOpen && selectedTableForQr && !selectedTableForQr.qrCode) {
      setIsLoadingQrs(true);
      setError(null);
      getUnassignedQrCodes()
        .then(data => setUnassignedQrs(data))
        .catch((err: unknown) => {
          console.error("Failed to fetch unassigned QR codes:", err);
          const errorMessage = err instanceof Error ? err.message : 
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to load unassigned QR codes.";
          setError(errorMessage);
        })
        .finally(() => setIsLoadingQrs(false));
    }
  }, [isQrModalOpen, selectedTableForQr, setError]);

  const handleAssignQrCode = async (qrCodeId: string) => {
    if (!selectedTableForQr) return;
    setIsUpdatingQr(true);
    setError(null);
    try {
      await assignQrCodeToTable(selectedTableForQr.id as string, qrCodeId);
      await refreshTables(); // Refresh to show updated table with QR
      // Update selectedTableForQr to reflect new QR, or simply close and let user reopen
      // const updatedTable = Array.isArray(tables) ? tables.find(t => t.id === selectedTableForQr.id) : null; // This might not have the QR yet if refreshTables is async
      // A better approach might be to have assignQrCodeToTable return the updated table
      // For now, just refresh and close, or refresh and then find.
      // Let's assume refreshTables updates the `tables` prop, and we need to find the new table from there.
      // This will be handled if the user re-opens the QR modal.
      closeQrModal();
    } catch (err) {
      console.error('Failed to assign QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to assign QR code.';
      setError(errorMessage);
    } finally {
      setIsUpdatingQr(false);
    }
  };

  const handleRemoveQrCode = async () => {
    if (!selectedTableForQr || !selectedTableForQr.qrCode) return;
    setIsUpdatingQr(true);
    setError(null);
    try {
      await removeQrCodeFromTable(selectedTableForQr.id as string);
      await refreshTables();
       // Similar to assign, update local state or close
      closeQrModal();
    } catch (err) {
      console.error('Failed to remove QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to remove QR code.';
      setError(errorMessage);
    } finally {
      setIsUpdatingQr(false);
    }
  };

  // Effect to clear tables if selected section is removed
   useEffect(() => {
    if (!selectedSection) {
      setTables([]);
    }
    // No need to fetch here, ManageLayoutPage's useEffect for selectedSection handles it
  }, [selectedSection, setTables]);


  if (!selectedSection) {
    return <p className="text-center text-[var(--color-text-secondary)] py-4">Select a section to manage tables.</p>;
  }

  if (isLoadingInitial) {
    return <p className="text-center text-[var(--color-text-secondary)] py-4">Loading tables for {selectedSection.name}...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAddModal} variant="primary" size="small" disabled={!selectedSection}>
          Add Table
        </Button>
      </div>

      {Array.isArray(tables) && tables.length === 0 && !isLoadingInitial && (
         <p className="text-center text-[var(--color-text-secondary)] py-4">No tables found. Add one!</p>
      )}

      {!Array.isArray(tables) && !isLoadingInitial && (
         <p className="text-center text-[var(--color-text-warning)] py-4">Error loading tables. Please try refreshing.</p>
      )}

      <ul className="space-y-2">
        {Array.isArray(tables) && tables.map(table => (
          <li
            key={table.id}
            className={`p-3 rounded-md transition-colors
                        bg-[var(--color-background-default)] hover:bg-[var(--color-background-hover)] shadow
                        border border-[var(--color-border-default)] flex justify-between items-center group`}
          >
            <div>
              <span className="font-medium text-[var(--color-text-primary)]">{table.name}</span>
              <small className="block text-[var(--color-text-secondary)]">
                QR: {table.qrCode ? <span className="font-semibold text-[var(--color-primary)]">{table.qrCode.code}</span> : 'None'}
              </small>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
               <Button onClick={() => openQrModal(table)} variant="secondary" size="small">
                QR
              </Button>
              <Button onClick={() => openEditModal(table)} variant="secondary" size="small">
                Edit
              </Button>
              <Button onClick={() => openDeleteConfirm(table)} variant="secondary" size="small">
                Del
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {/* Add/Edit Table Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? 'Edit Table' : 'Add New Table'}>
        <form onSubmit={handleSubmitTable} className="space-y-4">
          <div>
            <label htmlFor="tableName" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              Table Name:
            </label>
            <input
              type="text"
              id="tableName"
              name="name"
              value={currentTableData.name || ''}
              onChange={handleTableInputChange}
              required
              className="w-full p-2 border border-[var(--color-border-default)] rounded-md
                         focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]
                         bg-[var(--color-background-input)] text-[var(--color-text-input)]"
              disabled={isSubmitting}
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <Button type="button" onClick={closeModal} variant="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || !selectedSection}>
              {isSubmitting ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Table')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Table Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} onClose={closeDeleteConfirm} title="Confirm Delete">
        <p className="mb-4 text-[var(--color-text-primary)]">Are you sure you want to delete table "<strong>{tableToDelete?.name}</strong>"?</p>
        <div className="flex justify-end space-x-3">
          <Button type="button" onClick={closeDeleteConfirm} variant="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTable} variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>

      {/* Manage QR Code Modal */}
      {selectedTableForQr && (
        <Modal isOpen={isQrModalOpen} onClose={closeQrModal} title={`Manage QR for Table: ${selectedTableForQr.name}`}>
          <div className="space-y-3">
            {selectedTableForQr.qrCode ? (
              <div className="space-y-2">
                <p className="text-[var(--color-text-primary)]">Current QR Code: <strong className="text-[var(--color-primary)]">{selectedTableForQr.qrCode.code}</strong></p>
                {selectedTableForQr.qrCode.url && (
                  <a href={selectedTableForQr.qrCode.url} target="_blank" rel="noopener noreferrer" className="text-[var(--color-text-link)] hover:underline text-sm">
                    View QR Target
                  </a>
                )}
                <div className="pt-2">
                  <Button onClick={handleRemoveQrCode} variant="secondary" size="small" disabled={isUpdatingQr}>
                    {isUpdatingQr ? 'Removing...' : 'Remove QR Code'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[var(--color-text-primary)]">No QR code assigned to this table.</p>
                {isLoadingQrs ? <p className="text-[var(--color-text-secondary)]">Loading unassigned QR codes...</p> : (
                  unassignedQrs.length > 0 ? (
                    <>
                      <p className="text-sm text-[var(--color-text-secondary)]">Available QR Codes to assign:</p>
                      <ul className="max-h-40 overflow-y-auto border border-[var(--color-border-default)] rounded-md divide-y divide-[var(--color-border-default)]">
                        {unassignedQrs.map(qr => (
                          <li key={qr.id}
                              onClick={() => handleAssignQrCode(qr.id as string)}
                              className="p-2 cursor-pointer hover:bg-[var(--color-background-hover)] text-[var(--color-text-primary)]"
                          >
                            {qr.code}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : <p className="text-[var(--color-text-secondary)]">No unassigned QR codes available.</p>
                )}
              </div>
            )}
            <div className="flex justify-end pt-3">
              <Button type="button" onClick={closeQrModal} variant="secondary" disabled={isUpdatingQr}>
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TableListComponent;
